import { SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';
import Swiper from 'swiper/bundle';

// Extra vertical padding (px) added to the tallest slide's measured content
// height, so every slide lands on the same, slightly roomier height.
const PADDING_ADDITION = 48;

// Space between slides, in pixels.
const SPACE_BETWEEN = 25;

/**
 * A class for the Image Caption Carousel module. Wraps a Swiper instance and, on
 * init and resize, levels every slide to the tallest slide's content height.
 */
export default class ImageCaptionCarousel extends MessageBus {
  /**
   * Constructor function of the ImageCaptionCarousel class. Must receive one
   * argument, a DOM node representing the module's container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node that is passed into the constructor
     */
    this.element = element;

    /**
     * The Swiper instance, kept so destroy() can tear it down
     * @property {?Swiper} swiper The Swiper instance created in enable()
     */
    this.swiper = null;

    this.init();
  }

  /**
   * Initializes the view by calling the functions to create DOM references,
   * setup event handlers and then create the Swiper instance.
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.swiperEl = this.element.querySelector(
      SELECTORS.IMAGE_CAPTION_CAROUSEL_SWIPER
    );
    this.nextEl = this.element.querySelector(
      SELECTORS.IMAGE_CAPTION_CAROUSEL_NEXT
    );
    this.prevEl = this.element.querySelector(
      SELECTORS.IMAGE_CAPTION_CAROUSEL_PREV
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleSwiperInit = this.onSwiperInit.bind(this);
    this.handleSwiperResize = this.onSwiperResize.bind(this);

    return this;
  }

  /**
   * Create the Swiper instance to enable interaction with the view.
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.swiperEl) return this;

    this.swiper = new Swiper(this.swiperEl, {
      // 'auto' at every breakpoint lets Swiper measure each slide's real
      // rendered width (the cards are capped at 320px on mobile via CSS). With
      // a fixed slidesPerView of 1, Swiper instead forces the slide width to the
      // full container width, which disagrees with the CSS max-width and breaks
      // its end-of-carousel detection, letting users scroll into a blank slide.
      slidesPerView: 'auto',
      spaceBetween: SPACE_BETWEEN,
      navigation: {
        nextEl: this.nextEl,
        prevEl: this.prevEl
      },
      on: {
        init: this.handleSwiperInit,
        resize: this.handleSwiperResize
      }
    });

    return this;
  }

  /**
   * Tears the Swiper instance down.
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   * @chainable
   */
  destroy() {
    if (this.swiper) this.swiper.destroy(true, true);

    return this;
  }

  /**
   * Levels the slide heights once Swiper has initialised.
   * @param {Swiper} swiper The Swiper instance for this module
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   */
  onSwiperInit(swiper) {
    return this.setSlideHeightsToMax(swiper);
  }

  /**
   * Re-levels the slide heights whenever Swiper reports a resize.
   * @param {Swiper} swiper The Swiper instance for this module
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   */
  onSwiperResize(swiper) {
    return this.setSlideHeightsToMax(swiper);
  }

  /**
   * Measures the tallest slide's content, then sets every slide's content to
   * that height so the cards line up.
   * @param {Swiper} swiper The Swiper instance whose slides to level
   * @returns {ImageCaptionCarousel} A reference to the current instance of the class
   */
  setSlideHeightsToMax(swiper) {
    let maxHeight = 0;

    swiper.slides.forEach((slide) => {
      const content = slide.querySelector(
        SELECTORS.IMAGE_CAPTION_CAROUSEL_TEAM_MEMBER
      );

      if (!content) return;

      content.style.height = ''; // Reset before measuring
      maxHeight = Math.max(maxHeight, content.clientHeight + PADDING_ADDITION);
    });

    swiper.slides.forEach((slide) => {
      const content = slide.querySelector(
        SELECTORS.IMAGE_CAPTION_CAROUSEL_TEAM_MEMBER
      );

      if (content) content.style.height = `${maxHeight}px`;
    });

    return this;
  }
}
